import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Mic, ArrowLeft, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { RecordingControls } from "@/components/meeting/RecordingControls";
import { TranscriptPreview } from "@/components/meeting/TranscriptPreview";

const UPLOAD_CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — safe under proxy limits

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function MeetingRoomPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  // idle | recording | paused | uploading | processing | done | failed
  const [recState, setRecState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [processing, setProcessing] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/meetings/${id}/processing-status`);
        setProcessing(data.processing);
        if (data.processing?.status === "completed") {
          clearInterval(pollRef.current);
          const { data: m } = await api.get(`/meetings/${id}`);
          setMeeting(m);
          setTranscript(m.transcript);
          setRecState("done");
          toast.success("Transcription et résumé IA prêts !");
        } else if (data.processing?.status === "failed") {
          clearInterval(pollRef.current);
          setErrorMsg(data.processing?.error || "Le traitement a échoué.");
          setRecState("failed");
          toast.error("Le traitement a échoué. Votre audio est sauvegardé — cliquez sur Réessayer.");
        }
      } catch (err) {
        // network hiccup — keep polling, the background job continues server-side
        console.warn("Polling processing-status failed, will retry:", err?.message);
      }
    }, 3000);
  }, [id]);

  useEffect(() => {
    api.get(`/meetings/${id}`).then(({ data }) => {
      setMeeting(data);
      if (data.transcript) setTranscript(data.transcript);
      const proc = data.processing;
      if (proc?.status === "processing") {
        setProcessing(proc);
        setRecState("processing");
        startPolling();
      } else if (proc?.status === "failed" && !data.transcript) {
        setErrorMsg(proc.error || "Le traitement a échoué.");
        setRecState("failed");
      } else if (data.transcript) {
        setRecState("done");
      }
    }).catch((err) => console.error("Failed to load meeting:", err));
    return () => {
      stopStream();
      clearInterval(pollRef.current);
    };
  }, [id, startPolling]);

  const stopStream = () => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    setErrorMsg("");
    setTranscript(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setRecState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      await api.post(`/meetings/${id}/start`);
    } catch (err) {
      toast.error("Impossible d'accéder au micro. Vérifiez les permissions du navigateur.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setRecState("paused");
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setRecState("recording");
    }
  };

  const stopAndTranscribe = async () => {
    if (!mediaRecorderRef.current) return;
    if (elapsed < 3) {
      toast.warning("Enregistrez au moins 3 secondes avant d'arrêter.");
      return;
    }
    setRecState("uploading");
    setUploadPct(0);
    clearInterval(timerRef.current);

    await new Promise((resolve) => {
      mediaRecorderRef.current.onstop = resolve;
      mediaRecorderRef.current.stop();
    });
    stopStream();

    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 2000) {
      setRecState("idle");
      setErrorMsg("Enregistrement trop court ou silencieux. Réessayez en parlant plus distinctement.");
      toast.error("Audio insuffisant. Réessayez.");
      return;
    }

    try {
      const totalChunks = Math.ceil(blob.size / UPLOAD_CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const part = blob.slice(i * UPLOAD_CHUNK_SIZE, (i + 1) * UPLOAD_CHUNK_SIZE);
        const fd = new FormData();
        fd.append("chunk", part, `part_${i}.bin`);
        fd.append("chunk_index", String(i));
        let attempt = 0;
        // retry each chunk up to 3 times to survive flaky connections
        while (true) {
          try {
            await api.post(`/meetings/${id}/upload-chunk`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 120000,
            });
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 3) throw err;
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
        setUploadPct(Math.round(((i + 1) / totalChunks) * 100));
      }

      const fd = new FormData();
      fd.append("language", meeting?.language || "fr");
      fd.append("duration_seconds", String(elapsed));
      fd.append("total_chunks", String(Math.ceil(blob.size / UPLOAD_CHUNK_SIZE)));
      await api.post(`/meetings/${id}/process`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setProcessing({ status: "processing", step: "queued", progress: 2 });
      setRecState("processing");
      startPolling();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Erreur inconnue";
      setErrorMsg(detail);
      setRecState("idle");
      toast.error(detail);
    }
  };

  const retryProcessing = async () => {
    try {
      await api.post(`/meetings/${id}/retry-processing`);
      setErrorMsg("");
      setProcessing({ status: "processing", step: "queued", progress: 2 });
      setRecState("processing");
      startPolling();
      toast.info("Traitement relancé…");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Impossible de relancer le traitement";
      toast.error(detail);
      if (e?.response?.status === 404) {
        // audio no longer on server — must re-record
        setRecState("idle");
        setErrorMsg(detail);
      }
    }
  };

  const restartRecording = () => {
    setTranscript(null);
    setErrorMsg("");
    setElapsed(0);
    setUploadPct(0);
    setProcessing(null);
    setRecState("idle");
  };

  const processingLabel = () => {
    const step = processing?.step;
    if (step === "queued") return "Mise en file d'attente du traitement…";
    if (step === "summarizing") return "Génération du résumé IA par Claude…";
    if (step === "emailing") return "Envoi du compte-rendu PDF par email…";
    return "Transcription en cours par OpenAI Whisper…";
  };

  const isBusy = recState === "uploading" || recState === "processing";

  if (!meeting) return <div className="p-8 text-af-muted">Chargement…</div>;

  return (
    <div className="min-h-full p-6 md:p-10 max-w-4xl mx-auto">
      <Link to="/app/meetings" className="inline-flex items-center gap-2 text-sm text-af-secondary hover:text-af mb-8" data-testid="back-to-meetings">
        <ArrowLeft className="h-4 w-4" /> Retour aux réunions
      </Link>

      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Salle de réunion</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">{meeting.title}</h1>
        {meeting.description && <p className="text-af-secondary mt-2 leading-relaxed">{meeting.description}</p>}
      </div>

      {/* Recording / Status Card */}
      <Card className="p-12 bg-af-card border-af rounded-md shadow-none text-center mb-6" data-testid="recording-card">
        <div className="relative mx-auto mb-8 w-32 h-32 flex items-center justify-center">
          <div
            className={`absolute inset-0 rounded-full ${
              recState === "recording" ? "bg-terracotta/15 af-pulse" :
              recState === "done" ? "bg-[#F5EFD8]" :
              recState === "failed" ? "bg-[#FCE7E7]" : "bg-af-subtle"
            }`}
          />
          <div
            className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
              recState === "recording" ? "bg-terracotta" :
              recState === "done" ? "bg-forest" :
              recState === "failed" ? "bg-[#9F1239]" :
              "bg-af-subtle border border-af"
            }`}
          >
            {isBusy ? (
              <Loader2 className="h-10 w-10 text-terracotta animate-spin" />
            ) : recState === "done" ? (
              <CheckCircle2 className="h-10 w-10 text-white" />
            ) : recState === "failed" ? (
              <AlertTriangle className="h-10 w-10 text-white" />
            ) : (
              <Mic className={`h-10 w-10 ${recState === "recording" ? "text-white" : "text-af-muted"}`} />
            )}
          </div>
        </div>

        <div className="font-mono text-5xl text-af mb-2 tracking-wider" data-testid="timer-display">
          {formatTime(elapsed)}
        </div>
        <div className="text-sm text-af-muted mb-4 min-h-[20px]">
          {recState === "idle" && (errorMsg ? <span className="text-[#9F1239]">{errorMsg}</span> : "Prêt à enregistrer")}
          {recState === "recording" && (
            <span className="flex items-center justify-center gap-2 text-terracotta">
              <span className="h-2 w-2 rounded-full bg-terracotta af-pulse" />
              Enregistrement en cours
            </span>
          )}
          {recState === "paused" && "En pause"}
          {recState === "uploading" && (
            <span className="text-terracotta">Envoi sécurisé de l'audio vers le serveur… {uploadPct}%</span>
          )}
          {recState === "processing" && (
            <span className="text-terracotta">{processingLabel()}</span>
          )}
          {recState === "done" && <span className="text-forest font-semibold">Transcription et résumé prêts ! Le compte-rendu PDF a été envoyé par email.</span>}
          {recState === "failed" && (
            <span className="text-[#9F1239]" data-testid="processing-error-msg">
              Échec du traitement : {errorMsg}. Votre audio est sauvegardé sur le serveur, cliquez sur « Réessayer ».
            </span>
          )}
        </div>

        {/* Progress bar during upload / processing */}
        {isBusy && (
          <div className="max-w-md mx-auto mb-8" data-testid="processing-progress">
            <div className="h-2 w-full rounded-full bg-af-subtle border border-af overflow-hidden">
              <div
                className="h-full bg-terracotta transition-all duration-500"
                style={{ width: `${recState === "uploading" ? Math.round(uploadPct * 0.15) : Math.max(15, processing?.progress || 2)}%` }}
              />
            </div>
            <div className="text-xs text-af-muted mt-2">
              {recState === "uploading"
                ? `Envoi ${uploadPct}%`
                : `Progression : ${Math.max(15, processing?.progress || 2)}%`}
              {" · "}Vous pouvez laisser cette page ouverte, le traitement continue sur le serveur.
            </div>
          </div>
        )}

        <RecordingControls
          recState={recState}
          onStart={startRecording}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onStop={stopAndTranscribe}
          onRetry={retryProcessing}
          onRestart={restartRecording}
          onOpenMeeting={() => navigate(`/app/meetings/${id}`)}
        />

        <div className="mt-10 pt-8 border-t border-af text-xs text-af-muted max-w-md mx-auto">
          Langue : <span className="font-semibold text-af">{meeting.language?.toUpperCase()}</span>
          {" · "}Participants : <span className="font-semibold text-af">{(meeting.participants || []).length}</span>
        </div>
      </Card>

      {/* Live transcript preview */}
      {transcript && <TranscriptPreview transcript={transcript} meetingId={id} />}

      <div className="mt-6 text-xs text-af-muted text-center">
        Astuce : parlez clairement à 30 cm du micro. Pour de meilleurs résultats, enregistrez au moins 10 secondes.
      </div>
    </div>
  );
}
